import ReactGA from 'react-ga4';
import { hasAnalyticsConsent } from './cookieConsent';

const PREMIUM_PRICE_DKK = 119;
const ACQUISITION_STORAGE_KEY = 'vg_acq_context';
const CHECKOUT_CONTEXT_STORAGE_KEY = 'vg_checkout_context';
const GA_IDENTITY_STORAGE_KEY = 'vg_ga_identity';
const GA_IDENTITY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const GA_GET_TIMEOUT_MS = 1000;
const SEARCH_ENGINE_HOST = /(^|\.)(google|bing|yahoo|duckduckgo|yandex|baidu)(\.[a-z.]+)?$/i;
const CHECKOUT_RETURN_PATHS = new Set(['/payment-success', '/payment-cancel']);

// GA4 uses these names for campaign attribution. Funnel labels must not reuse them.
const RESERVED_EVENT_PARAM_RENAMES = {
  source: 'funnel_source',
  medium: 'funnel_medium',
  campaign: 'funnel_campaign',
  term: 'funnel_term',
  content: 'funnel_content',
};

export const CTA_TYPE_GUEST_REGISTRATION = 'guest_registration';
export const CTA_TYPE_GUEST_LOGIN = 'guest_login';
export const CTA_TYPE_AUTH_UNLOCK = 'authenticated_unlock';
export const CTA_TYPE_OPEN_UPSELL = 'open_upsell';

const isDevelopment = () => process.env.NODE_ENV === 'development';

let gaInitialized = false;

export function canTrack() {
  return hasAnalyticsConsent() && Boolean(process.env.REACT_APP_GA_MEASUREMENT_ID);
}

function getDomainContext() {
  return {
    domain_name: typeof window !== 'undefined' ? window.location.hostname : '',
    environment: isDevelopment() ? 'development' : 'production',
  };
}

function safeHostname(url) {
  if (!url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

export function classifyTrafficChannel({ utmSource, referrer, hostname } = {}) {
  if (utmSource) return 'campaign';
  const referrerHost = safeHostname(referrer);
  if (!referrerHost) return 'direct';
  if (hostname && referrerHost === hostname) return 'internal';
  if (SEARCH_ENGINE_HOST.test(referrerHost)) return 'organic';
  return 'referral';
}

export function captureAcquisitionContext() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return getAcquisitionContext();
  }
  try {
    if (sessionStorage.getItem(ACQUISITION_STORAGE_KEY)) {
      return getAcquisitionContext();
    }
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || '';
    const utmMedium = params.get('utm_medium') || '';
    const utmCampaign = params.get('utm_campaign') || '';
    const referrer = document.referrer || '';
    const hostname = window.location.hostname;
    const trafficChannel = classifyTrafficChannel({
      utmSource,
      referrer,
      hostname,
    });
    const context = {
      traffic_channel: trafficChannel,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      page_referrer: referrer,
    };
    sessionStorage.setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(context));
    return context;
  } catch {
    return getAcquisitionContext();
  }
}

export function getAcquisitionContext() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return { traffic_channel: 'direct' };
  }
  try {
    const stored = sessionStorage.getItem(ACQUISITION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore malformed storage
  }
  const params = new URLSearchParams(window.location.search);
  return {
    traffic_channel: classifyTrafficChannel({
      utmSource: params.get('utm_source') || '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      hostname: window.location.hostname,
    }),
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    page_referrer: typeof document !== 'undefined' ? document.referrer : '',
  };
}

export function persistCheckoutContext(context = {}) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(CHECKOUT_CONTEXT_STORAGE_KEY, JSON.stringify({
      ...context,
      saved_at: Date.now(),
    }));
  } catch {
    // ignore quota / private mode
  }
}

export function consumeCheckoutContext() {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(CHECKOUT_CONTEXT_STORAGE_KEY);
    if (!raw) return {};
    sessionStorage.removeItem(CHECKOUT_CONTEXT_STORAGE_KEY);
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

export function resolveCtaType({ cta, userTier } = {}) {
  if (cta === 'register') return CTA_TYPE_GUEST_REGISTRATION;
  if (cta === 'login') return CTA_TYPE_GUEST_LOGIN;
  if (cta === 'upgrade' || cta === 'unlock') return CTA_TYPE_AUTH_UNLOCK;
  if (cta === 'open_upsell') return CTA_TYPE_OPEN_UPSELL;
  if (!userTier || userTier === 'guest') return CTA_TYPE_GUEST_REGISTRATION;
  return CTA_TYPE_AUTH_UNLOCK;
}

export function getFunnelContext(extra = {}) {
  const acquisition = getAcquisitionContext();
  return {
    ...getDomainContext(),
    traffic_channel: acquisition.traffic_channel || 'direct',
    utm_source: acquisition.utm_source || '',
    utm_campaign: acquisition.utm_campaign || '',
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...extra,
  };
}

export function isGaSessionId(value) {
  return /^\d{1,20}$/.test(String(value ?? ''));
}

/**
 * Keep funnel labels off reserved GA4 campaign/session fields.
 * A non-numeric session id (Stripe's cs_... value) is stored as
 * checkout_session_id so it cannot replace the browser session.
 */
export function sanitizeGaEventParams(params = {}) {
  const next = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (key === 'session_id') {
      if (isGaSessionId(value)) {
        next.session_id = String(value);
      } else if (value !== '') {
        next.checkout_session_id = String(value);
      }
      return;
    }
    const renamed = RESERVED_EVENT_PARAM_RENAMES[key] || key;
    if (value === '' && (key in RESERVED_EVENT_PARAM_RENAMES || key.startsWith('utm_'))) {
      return;
    }
    next[renamed] = value;
  });
  return next;
}

export function readGaClientIdFromCookie(cookieString) {
  const source = cookieString !== undefined
    ? cookieString
    : (typeof document !== 'undefined' ? document.cookie : '');
  if (!source) return null;
  const match = source.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)(?:;|$)/);
  return match ? match[1] : null;
}

export function readGaSessionIdFromCookie(measurementId, cookieString) {
  if (!measurementId) return null;
  const source = cookieString !== undefined
    ? cookieString
    : (typeof document !== 'undefined' ? document.cookie : '');
  if (!source) return null;
  const container = String(measurementId).replace(/^G-/, '');
  const match = source.match(new RegExp(`(?:^|;\\s*)_ga_${container}=([^;]+)`));
  if (!match) return null;
  let value = match[1];
  try {
    value = decodeURIComponent(value);
  } catch {
    // keep the raw cookie value
  }
  const gs1 = value.match(/^GS1\.\d+\.(\d+)/);
  if (gs1 && isGaSessionId(gs1[1])) return gs1[1];
  const gs2 = value.match(/^GS2\.\d+\.s(\d+)(?:\$|$)/);
  if (gs2 && isGaSessionId(gs2[1])) return gs2[1];
  return null;
}

function readGaFieldFromCookie(field, measurementId) {
  if (field === 'client_id') return readGaClientIdFromCookie();
  if (field === 'session_id') return readGaSessionIdFromCookie(measurementId);
  return null;
}

function gtagGet(field) {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const fallback = () => readGaFieldFromCookie(field, measurementId);
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (!measurementId || typeof window.gtag !== 'function') {
    return Promise.resolve(fallback());
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      resolve(value || fallback() || null);
    };
    const timer = setTimeout(() => finish(null), GA_GET_TIMEOUT_MS);
    try {
      window.gtag('get', measurementId, field, (value) => {
        clearTimeout(timer);
        finish(value);
      });
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

export function readStoredGaIdentity() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(GA_IDENTITY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const age = Date.now() - Number(parsed?.saved_at || 0);
    if (!Number.isFinite(age) || age < 0 || age > GA_IDENTITY_MAX_AGE_MS) {
      return null;
    }
    const clientId = parsed.client_id ? String(parsed.client_id) : '';
    const sessionId = isGaSessionId(parsed.session_id) ? String(parsed.session_id) : '';
    if (!clientId && !sessionId) return null;
    return { client_id: clientId, session_id: sessionId, saved_at: parsed.saved_at };
  } catch {
    return null;
  }
}

export async function captureGaIdentity() {
  const [clientId, sessionId] = await Promise.all([
    gtagGet('client_id'),
    gtagGet('session_id'),
  ]);
  const identity = {
    client_id: clientId || '',
    session_id: isGaSessionId(sessionId) ? String(sessionId) : '',
    saved_at: Date.now(),
  };
  if (typeof sessionStorage !== 'undefined' && (identity.client_id || identity.session_id)) {
    try {
      sessionStorage.setItem(GA_IDENTITY_STORAGE_KEY, JSON.stringify(identity));
    } catch {
      // ignore quota / private mode
    }
  }
  return identity;
}

export function getCheckoutReturnIdentity() {
  if (typeof window === 'undefined') return null;
  if (!CHECKOUT_RETURN_PATHS.has(window.location.pathname)) return null;
  const stored = readStoredGaIdentity();
  if (!stored?.client_id || !stored.session_id) return null;
  return { client_id: stored.client_id, session_id: stored.session_id };
}

function devLog(message, data) {
  if (isDevelopment()) {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
}

function sendGa4Event(eventName, params = {}, { beacon = false } = {}) {
  if (!canTrack()) {
    return;
  }
  const payload = sanitizeGaEventParams({
    ...getFunnelContext(),
    ...params,
  });
  if (beacon) {
    payload.transport_type = 'beacon';
  }
  ReactGA.event(eventName, payload);
  devLog(`GA4 Event: ${eventName}`, payload);
}

export function flushGa4Event(eventName, params = {}, timeoutMs = 1500) {
  const payload = sanitizeGaEventParams({
    ...getFunnelContext(),
    ...params,
    transport_type: 'beacon',
  });

  if (!canTrack()) {
    return Promise.resolve(false);
  }

  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    sendGa4Event(eventName, params, { beacon: true });
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (sent) => {
      if (settled) return;
      settled = true;
      resolve(sent);
    };
    const timer = setTimeout(() => finish(true), timeoutMs);
    try {
      window.gtag('event', eventName, {
        ...payload,
        event_callback: () => {
          clearTimeout(timer);
          finish(true);
        },
      });
      devLog(`GA4 Event (flush): ${eventName}`, payload);
    } catch (error) {
      clearTimeout(timer);
      sendGa4Event(eventName, params, { beacon: true });
      finish(false);
    }
  });
}

export function resolveUserTier(user) {
  if (!user) return 'guest';
  if (user.is_premium_active === true) return 'premium';
  if (user.access_level === 'premium') return 'premium';
  return 'basis';
}

// Initialize Google Analytics with proper cross-domain tracking
export const initGA = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (!measurementId) {
    if (isDevelopment()) {
      console.warn('Google Analytics Measurement ID not found in environment variables');
    }
    return;
  }

  if (!hasAnalyticsConsent()) {
    return;
  }

  if (gaInitialized) {
    return;
  }

  const isDev = isDevelopment();
  const currentDomain = window.location.hostname;
  const primaryDomain = process.env.REACT_APP_PRIMARY_DOMAIN || 'vuggestueguiden.dk';
  const secondaryDomain = process.env.REACT_APP_SECONDARY_DOMAIN || 'bornehaveguiden.dk';
  const restore = getCheckoutReturnIdentity();

  // index.html already calls gtag('config'). This second call must not send
  // another page_view, and on the Stripe return page it must keep the
  // client/session captured before the redirect.
  const gtagOptions = {
    send_page_view: false,
  };
  if (!isDev) {
    gtagOptions.linker = {
      domains: [primaryDomain, secondaryDomain],
    };
  }
  if (restore) {
    gtagOptions.client_id = restore.client_id;
    gtagOptions.session_id = restore.session_id;
  }

  // index.html already loaded gtag.js. react-ga4 would otherwise inject a
  // second copy and replace window.gtag, which can mint a new client id.
  if (typeof window.gtag === 'function') {
    ReactGA._hasLoadedGA = true;
  }

  ReactGA.initialize(measurementId, { gtagOptions });

  gaInitialized = true;
  captureAcquisitionContext();
  devLog(`Google Analytics initialized on ${currentDomain}`);
};

/**
 * @returns {Promise<string|null>}
 */
export function getGaClientId() {
  return gtagGet('client_id');
}

export function getGaSessionId() {
  return gtagGet('session_id');
}

export function setAnalyticsUser(user) {
  if (!canTrack()) {
    return;
  }

  if (!user) {
    clearAnalyticsUser();
    return;
  }

  ReactGA.set({ userId: String(user.id) });
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      user_tier: resolveUserTier(user),
    });
  }
}

export function clearAnalyticsUser() {
  if (!canTrack()) {
    return;
  }
  ReactGA.set({ userId: null });
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', {
      user_tier: 'guest',
    });
  }
}

// ===== FUNNEL EVENTS (GA4 recommended names) =====

export function trackSignUp(method, extra = {}) {
  sendGa4Event('sign_up', { method, ...extra });
  sendGa4Event('registration_complete', { method, ...extra });
}

export function trackLogin(method, extra = {}) {
  sendGa4Event('login', { method, ...extra });
}

export function trackRegistrationStart(extra = {}) {
  sendGa4Event('registration_start', {
    source: extra.source || 'register_page',
    has_upgrade_intent: extra.hasUpgradeIntent ? 'true' : 'false',
    promotion_name: extra.feature || 'general',
    institution_slug: extra.institutionSlug || '',
    user_tier: extra.userTier || 'guest',
    cta_type: CTA_TYPE_GUEST_REGISTRATION,
  });
}

export function trackGenerateLead(institutionCount) {
  sendGa4Event('generate_lead', { institution_count: institutionCount });
}

export function trackViewPromotion(feature, userTier, extra = {}) {
  sendGa4Event('view_promotion', {
    promotion_name: feature || 'general',
    user_tier: userTier,
    placement: extra.placement || '',
    institution_slug: extra.institutionSlug || '',
    source: extra.source || extra.placement || 'unknown',
  });
}

export function trackSelectPromotion(cta, feature, extra = {}) {
  const userTier = extra.userTier;
  sendGa4Event('select_promotion', {
    promotion_name: feature || 'general',
    cta,
    cta_type: extra.ctaType || resolveCtaType({ cta, userTier }),
    user_tier: userTier || 'guest',
    placement: extra.placement || '',
    institution_slug: extra.institutionSlug || '',
    source: extra.source || extra.placement || 'unknown',
  });
}

export function trackFeatureUse(feature, action, userTier, params = {}) {
  sendGa4Event('feature_use', {
    feature_name: feature,
    action,
    user_tier: userTier,
    ...params,
  });
}

export function trackInstitutionView({
  slug,
  name,
  district,
  institutionType,
  userTier,
} = {}) {
  sendGa4Event('institution_view', {
    institution_slug: slug || '',
    institution_name: name || '',
    district: district || '',
    institution_type: institutionType || '',
    user_tier: userTier || 'guest',
  });
}

export function trackPremiumView({
  feature,
  institutionSlug,
  userTier,
  placement,
  source,
} = {}) {
  sendGa4Event('premium_view', {
    promotion_name: feature || 'general',
    institution_slug: institutionSlug || '',
    user_tier: userTier || 'guest',
    placement: placement || 'unknown',
    source: source || placement || 'unknown',
    cta_type: userTier === 'guest' ? CTA_TYPE_GUEST_REGISTRATION : CTA_TYPE_AUTH_UNLOCK,
  });
}

export function trackPremiumCtaClick({
  feature,
  cta,
  institutionSlug,
  userTier,
  placement,
  source,
} = {}) {
  const ctaType = resolveCtaType({ cta, userTier });
  sendGa4Event('premium_cta_click', {
    promotion_name: feature || 'general',
    cta: cta || (userTier === 'guest' ? 'register' : 'upgrade'),
    cta_type: ctaType,
    institution_slug: institutionSlug || '',
    user_tier: userTier || 'guest',
    placement: placement || 'unknown',
    source: source || placement || 'unknown',
  });
}

export function trackUpgradeIntent(extra = {}) {
  sendGa4Event('upgrade', {
    promotion_name: extra.feature || 'general',
    cta: extra.cta || 'upgrade',
    cta_type: CTA_TYPE_AUTH_UNLOCK,
    user_tier: extra.userTier || 'basis',
    institution_slug: extra.institutionSlug || '',
    placement: extra.placement || '',
    source: extra.source || extra.placement || 'unknown',
  });
}

export function trackComparisonStartEvent({ institutionCount, source, slugs } = {}) {
  sendGa4Event('comparison_start', {
    institution_count: institutionCount || 0,
    source: source || 'unknown',
    institution_slugs: slugs || '',
  });
}

export function trackComparisonView({ institutionCount, slugs } = {}) {
  sendGa4Event('comparison_view', {
    institution_count: institutionCount || 0,
    institution_slugs: slugs || '',
  });
}

export function trackPricingView({ feature, userTier, placement } = {}) {
  sendGa4Event('pricing_view', {
    promotion_name: feature || 'general',
    user_tier: userTier || 'guest',
    placement: placement || 'unknown',
    value: PREMIUM_PRICE_DKK,
    currency: 'DKK',
  });
}

function checkoutEventParams(sourceOrContext = {}) {
  const context = typeof sourceOrContext === 'string'
    ? { source: sourceOrContext }
    : (sourceOrContext || {});
  const params = {
    value: PREMIUM_PRICE_DKK,
    currency: 'DKK',
    source: context.source || 'unknown',
    cta: context.cta || 'upgrade',
    cta_type: CTA_TYPE_AUTH_UNLOCK,
    user_tier: context.userTier || 'basis',
    promotion_name: context.feature || 'general',
    institution_slug: context.institutionSlug || '',
    placement: context.placement || '',
  };
  // Stripe's cs_... id is not a GA4 session id. Keep it off the reserved field.
  const stripeSessionId = context.sessionId || context.checkoutSessionId || '';
  if (stripeSessionId && !isGaSessionId(stripeSessionId)) {
    params.checkout_session_id = String(stripeSessionId);
  }
  return params;
}

export function trackBeginCheckout(sourceOrContext) {
  const params = checkoutEventParams(sourceOrContext);
  persistCheckoutContext(params);
  sendGa4Event('begin_checkout', params, { beacon: true });
  sendGa4Event('checkout_start', params, { beacon: true });
}

export async function flushBeginCheckout(sourceOrContext) {
  const params = checkoutEventParams(sourceOrContext);
  persistCheckoutContext(params);
  await flushGa4Event('begin_checkout', params);
  sendGa4Event('checkout_start', params, { beacon: true });
}

export function trackStripeRedirect(sourceOrContext) {
  const params = checkoutEventParams(sourceOrContext);
  sendGa4Event('stripe_redirect', params, { beacon: true });
}

export function trackCheckoutError(sourceOrContext, errorMessage) {
  const params = checkoutEventParams(sourceOrContext);
  sendGa4Event('checkout_error', {
    ...params,
    error_message: String(errorMessage || 'unknown').slice(0, 100),
  });
}

export function trackPurchase(transactionId, extra = {}) {
  const stored = extra.skipStoredContext ? {} : consumeCheckoutContext();
  const identity = readStoredGaIdentity();
  const value = extra.value || PREMIUM_PRICE_DKK;
  const params = {
    value,
    currency: extra.currency || 'DKK',
    source: extra.source || stored.source || 'unknown',
    cta_type: CTA_TYPE_AUTH_UNLOCK,
    promotion_name: extra.feature || stored.promotion_name || 'general',
    institution_slug: extra.institutionSlug || stored.institution_slug || '',
    placement: extra.placement || stored.placement || '',
    items: [
      {
        item_id: 'premium_annual',
        item_name: 'Premium',
        price: value,
        quantity: 1,
      },
    ],
  };
  if (transactionId) {
    params.transaction_id = transactionId;
  }
  const gaSessionId = extra.gaSessionId || extra.ga_session_id || identity?.session_id;
  if (isGaSessionId(gaSessionId)) {
    params.session_id = String(gaSessionId);
  }
  sendGa4Event('purchase', params);
}

export function trackCheckoutAbandon() {
  sendGa4Event('checkout_abandon');
}

// Track page views with domain and environment information
export const logPageView = (path) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;

  ReactGA.send({
    hitType: 'pageview',
    page: path,
    location: window.location.href,
    title: document.title,
    custom_parameters: {
      domain_name: currentDomain,
      environment: isDevelopment() ? 'development' : 'production',
    },
  });

  devLog(`GA Page View: ${path} on ${currentDomain}`);
};

// Track custom events with domain and environment context
export const logEvent = (category, action, label, value = null) => {
  if (!canTrack()) {
    return;
  }

  const eventData = {
    category,
    action,
    label,
    custom_parameters: getDomainContext(),
  };

  if (value !== null) {
    eventData.value = value;
  }

  ReactGA.event(eventData);
  devLog(`GA Event: ${category} - ${action} - ${label}`);
};

export const logDomainEvent = (eventType, details) => {
  const currentDomain = window.location.hostname;
  logEvent('Domain Usage', eventType, `${currentDomain}: ${details}`);
};

export const logInstitutionComparison = (institutionCount) => {
  const currentDomain = window.location.hostname;
  logEvent('Institution', 'compare', `${currentDomain}_comparison`, institutionCount);
};

export const logSearchFilter = (filterType, filterValue) => {
  logEvent('Search', 'filter', `${filterType}: ${filterValue}`, null);
};

export const logUserEngagement = (engagementType, details) => {
  logEvent('User Engagement', engagementType, details);
};

export const logPaymentEvent = (eventType, plan = null) => {
  const currentDomain = window.location.hostname;
  const label = plan ? `${currentDomain}_${plan}` : currentDomain;
  logEvent('Payment', eventType, label);
};

export const logError = (errorType, errorMessage) => {
  const currentDomain = window.location.hostname;
  logEvent('Error', errorType, `${currentDomain}: ${errorMessage}`);
};

// ===== COMPARISON PAGE SPECIFIC TRACKING =====

export const logComparisonStart = (institutions, apiResponseTime = null) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;
  const institutionNames = institutions.map((inst) => inst.name).join(', ');
  const institutionCount = institutions.length;

  const eventData = {
    category: 'Comparison',
    action: 'start',
    label: `${currentDomain}_comparison_start`,
    custom_parameters: {
      ...getDomainContext(),
      institution_count: institutionCount,
      institution_names: institutionNames,
    },
  };

  if (apiResponseTime !== null) {
    eventData.custom_parameters.api_response_time = apiResponseTime;
  }

  ReactGA.event(eventData);
  sendGa4Event('comparison_start', {
    institution_count: institutionCount,
    institution_names: institutionNames,
    source: 'comparison_basket',
  });
  devLog(`GA Comparison Start: ${institutionCount} institutions`);
};

export const logComparisonInteraction = (interactionType, details, interactionCount, metricKey = null) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;

  const eventData = {
    category: 'Comparison',
    action: 'interaction',
    label: `${currentDomain}_${interactionType}`,
    custom_parameters: {
      ...getDomainContext(),
      interaction_count: interactionCount,
    },
  };

  if (metricKey) {
    eventData.custom_parameters.metric_key = metricKey;
  }

  ReactGA.event(eventData);
  devLog(`GA Comparison Interaction: ${interactionType}`, details);
};

export const logScrollTracking = (scrollDepth, scrollPercentage, sessionDuration, isScrollMilestone = false) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;
  const action = isScrollMilestone ? 'scroll_milestone' : 'scroll_update';

  ReactGA.event({
    category: 'Comparison',
    action,
    label: `${currentDomain}_scroll_${Math.round(scrollPercentage)}%`,
    custom_parameters: {
      ...getDomainContext(),
      scroll_depth: scrollDepth,
      scroll_percentage: scrollPercentage,
      session_duration: sessionDuration,
    },
  });
};

export const logComparisonSession = (sessionData) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;

  const eventData = {
    category: 'Comparison',
    action: 'session_end',
    label: `${currentDomain}_session_summary`,
    custom_parameters: {
      ...getDomainContext(),
      institution_count: sessionData.institutionCount,
      institution_names: sessionData.institutionNames,
      interaction_count: sessionData.totalInteractions,
      scroll_depth: sessionData.maxScrollDepth,
      scroll_percentage: sessionData.maxScrollPercentage,
      session_duration: sessionData.sessionDuration,
    },
  };

  if (sessionData.apiResponseTime) {
    eventData.custom_parameters.api_response_time = sessionData.apiResponseTime;
  }

  ReactGA.event(eventData);
  devLog('GA Comparison Session Summary', sessionData);
};

export const logApiResponseTime = (endpoint, responseTime, additionalData = {}) => {
  if (!canTrack()) {
    return;
  }

  const currentDomain = window.location.hostname;

  ReactGA.event({
    category: 'API',
    action: 'response_time',
    label: `${currentDomain}_${endpoint}`,
    value: Math.round(responseTime),
    custom_parameters: {
      ...getDomainContext(),
      api_response_time: responseTime,
      ...additionalData,
    },
  });

  devLog(`GA API Response Time: ${endpoint} - ${responseTime}ms`);
};

// ===== GOOGLE ADS CONVERSION TRACKING =====

export const trackGoogleAdsConversion = (conversionLabel, conversionData = {}) => {
  const googleAdsId = process.env.REACT_APP_GOOGLE_ADS_ID;

  if (!googleAdsId || !canTrack()) {
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', 'conversion', {
        send_to: `${googleAdsId}/${conversionLabel}`,
        ...conversionData,
      });
      devLog(`Google Ads Conversion: ${conversionLabel}`, conversionData);
    } catch (error) {
      console.error('Error tracking Google Ads conversion:', error);
    }
  }
};

export const trackGoogleAdsPageView = () => {
  const googleAdsId = process.env.REACT_APP_GOOGLE_ADS_ID;

  if (!googleAdsId || !canTrack()) {
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', 'page_view', {
        send_to: googleAdsId,
      });
      devLog('Google Ads Page View tracked');
    } catch (error) {
      console.error('Error tracking Google Ads page view:', error);
    }
  }
};

export const trackGoogleAdsEvent = (eventName, eventData = {}) => {
  const googleAdsId = process.env.REACT_APP_GOOGLE_ADS_ID;

  if (!googleAdsId || !canTrack()) {
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', eventName, {
        send_to: googleAdsId,
        ...eventData,
      });
      devLog(`Google Ads Event: ${eventName}`, eventData);
    } catch (error) {
      console.error('Error tracking Google Ads event:', error);
    }
  }
};
